import { useTranslation } from "react-i18next";
import { chapters, categories, faqs } from "@/mocks/chapters";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";

function HomeSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "火影漫画",
        url: "https://naruto-manga.com",
        description: "火影漫画官方正版全集在线阅读平台，完整收录鸣人从忍者学校到火影之路的全部章节",
        foundingDate: "2024",
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          availableLanguage: ["Chinese"],
        },
      },
      {
        "@type": "WebSite",
        name: "火影漫画",
        url: "https://naruto-manga.com",
        description: "火影漫画官方正版全集在线阅读平台",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://naruto-manga.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        name: "火影漫画 - 官方正版全集在线阅读",
        description: "火影漫画是日本漫画史上最经典的少年热血巨作，完整收录鸣人从忍者学校到火影之路的全部章节。",
        url: "https://naruto-manga.com",
        isPartOf: { "@id": "https://naruto-manga.com" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function Home() {
  const { t } = useTranslation();

  const hotChapters = chapters.filter((ch) => ch.isHot).slice(0, 6);
  const classicChapters = chapters.filter((ch) => ch.isClassic).slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <HomeSchema />
      <ScrollToTop />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Naruto%20Uzumaki%20silhouette%20standing%20on%20Hokage%20rock%20mountain%20at%20sunset%20with%20Konoha%20village%20below%20warm%20orange%20and%20red%20sky%20epic%20anime%20art%20style%20cinematic%20composition%20dramatic%20clouds&width=1600&height=900&seq=naruto-hero-bg&orientation=landscape"
            alt="火影漫画 - 鸣人站在火影岩上"
            title="火影漫画官方正版全集在线阅读"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            {t("home.hero.title")}
          </h1>
          <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t("home.hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/category/ninja-academy"
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("home.hero.cta")}
            </a>
            <a
              href="/about"
              className="px-8 py-3 border border-white/40 text-white rounded-full font-semibold text-sm hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              {t("home.hero.about")}
            </a>
          </div>
        </div>
      </section>

      {/* Category Section */}
      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t("home.categories.title")}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              {t("home.categories.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                className="group p-5 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100/70 rounded-lg mb-3 group-hover:bg-primary-500 transition-colors">
                  <i className={`${cat.icon} text-lg text-primary-600 group-hover:text-background-50 transition-colors`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                  {cat.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Chapters Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t("home.hot.title")}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              {t("home.hot.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {hotChapters.map((ch) => (
              <a
                key={ch.id}
                href={`/article/${ch.slug}`}
                className="group flex gap-4 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300"
              >
                <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
                  <img
                    src={ch.coverImage}
                    alt={ch.title}
                    title={ch.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col justify-between min-w-0">
                  <div>
                    <span className="text-xs text-accent-500 font-medium">
                      {ch.category}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900 mt-1 mb-1 line-clamp-2 leading-snug">
                      {ch.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {ch.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {ch.views.toLocaleString()} {t("readCount")}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Classic Battles Section */}
      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t("home.classic.title")}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              {t("home.classic.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {classicChapters.map((ch, idx) => (
              <a
                key={ch.id}
                href={`/article/${ch.slug}`}
                className="group relative overflow-hidden rounded-lg"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={ch.coverImage}
                    alt={ch.title}
                    title={ch.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
                      #{idx + 1}
                    </span>
                    <span className="text-xs text-white/70">
                      {ch.category}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1">
                    {ch.title}
                  </h3>
                  <p className="text-xs text-white/70 line-clamp-1">
                    {ch.summary}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t("home.faq.title")}
            </h2>
            <p className="text-sm md:text-base text-foreground-600">
              {t("home.faq.subtitle")}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors">
                  <h4 className="text-sm font-semibold text-foreground-900 pr-4">
                    {faq.question}
                  </h4>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </div>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-24 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Naruto%20Uzumaki%20running%20forward%20with%20determined%20expression%20surrounded%20by%20warm%20golden%20light%20anime%20art%20style%20inspirational%20atmosphere%20soft%20bokeh%20background&width=1600&height=600&seq=naruto-cta-bg&orientation=landscape"
            alt="火影漫画 - 开启你的火影之旅"
            title="火影漫画阅读平台"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t("home.cta.title")}
          </h2>
          <p className="text-sm md:text-base text-white/80 mb-8">
            {t("home.cta.subtitle")}
          </p>
          <a
            href="/category/ninja-academy"
            className="inline-flex px-8 py-3 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            {t("home.cta.button")}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}